import { FC, useMemo } from 'react';

import { InfoOutlined } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';

import { ApplicantNO, Country } from 'src/api/zrm';
import CenteredTooltip from 'src/components/widgets/info/CenteredTooltip';
import { useCustomerDataContext } from 'src/contexts/auth/CustomerDataContext';
import ExternallyVerifiedCreditExtra from 'src/types/GR/ExternallyVerifiedCreditExtra';
import formatCurrency from 'src/utils/format/formatCurrency';

interface GrCreditsHeaderProps { }

const GrCreditsHeader: FC<GrCreditsHeaderProps> = (props) => {
  const { } = props;

  const { t } = useTranslation();
  const { customerData } = useCustomerDataContext();


  const credits = useMemo(() => (customerData as ApplicantNO)?.externally_verified_credits?.map(c => ({ ...c, uuid: v4() })) as ExternallyVerifiedCreditExtra[], [customerData]);
  const totalAmount = useMemo(() => credits?.reduce((prev, curr) => prev + curr.total_debt, 0) || 0, [credits]);
  const totalCreditLimit = useMemo(() => credits?.reduce((prev, curr) => prev + curr.creditLimit, 0) || 0, [credits]);

  return (
    <Grid
      item
      xs={12}
      sx={{ '& .MuiTypography-root': { fontWeight: 'bold' } }}
    >
      <Typography sx={{ fontSize: '2rem', mb: 1 }}>
        {t('My Credits')}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '1.25rem' }}>
          {t('Total Amount')}
        </Typography>
        <Typography sx={{ fontSize: '1.25rem' }}>
          {formatCurrency(totalAmount, Country.NO, 0)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '1.25rem' }}>
          <CenteredTooltip
            title={t('This is the combined limit of all your credit lines, like credit cards and overdrafts.')}
            enterTouchDelay={0}
            leaveTouchDelay={5000}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {t('Total Credit Limit')}
              <InfoOutlined
                sx={{ ml: 1 }}
                fontSize="small"
              />
            </Box>
          </CenteredTooltip>
        </Typography>
        <Typography sx={{ fontSize: '1.25rem' }}>
          {formatCurrency(totalCreditLimit, Country.NO, 0)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography>
          {t('Number of Credits')}
        </Typography>
        <Typography>
          {credits?.length || 0}
        </Typography>
      </Box>
    </Grid>
  );
};


export default GrCreditsHeader;